/**
 * Organization Document Mock Data Index
 * Centralized export for all Organization Document mock data
 * Used in Phase 1: UI-First approach
 */

// Feature flag helper - must be imported before re-exports
import { ORG_DOC_USE_MOCK_DATA } from '../Configurations/FeatureFlags';

// Import all mock data - Admin Configuration
export { default as mockVerticals, getVerticalById, getActiveVerticals } from './orgDocVerticals';
export { default as mockCategories, getCategoriesByVertical, getCategoryById, getActiveCategories } from './orgDocCategories';
export { default as mockDocumentTypes, getDocumentTypesByCategory, getDocumentTypesByVertical, getDocumentTypeById, getDocumentTypeByCode, getActiveDocumentTypes } from './orgDocTypes';
export { default as mockDocumentTypeFields, getFieldsByDocumentType, getFieldById } from './orgDocTypeFields';
export { default as mockAuthorities, getAuthoritiesByDocumentType, getAuthorityById, getDefaultAuthority } from './orgDocAuthorities';
export { default as mockRequiredRules, getRulesByVertical, getRuleById, getActiveRules, organizationTypes, conditionFields } from './orgDocRequiredRules';

// Import all mock data - Customer Documents (Phase 2)
export {
  default as mockDocuments,
  mockOrganizations,
  documentStatuses,
  extractionStatuses,
  mockDocumentVersions,
  getDocumentsByOrganization,
  getDocumentsByCategory,
  getDocumentSummary,
  getDocumentVersions
} from './orgDocDocuments';

export {
  default as mockRequiredDocumentsStatus,
  complianceStatuses,
  getRequiredDocumentsStatus,
  getRequirementsByCategory,
  getCriticalRequirements,
  calculateComplianceScore
} from './orgDocRequiredDocuments';

export {
  default as mockExtractionResults,
  fieldConfidenceLevel,
  mockCrossDocumentWarnings,
  simulateExtraction,
  getExtractionResult,
  getConfidenceColor,
  formatConfidence
} from './orgDocExtractionResults';

// Import all mock data - Tax Services (Phase 3)
export {
  default as mockTaxServices,
  serviceStatuses,
  filingPeriods,
  taxServiceCategories,
  mockTaxProfile,
  mockTaxServiceRequests,
  mockTaxDeadlines,
  getTaxServicesByCategory,
  getTaxServiceById,
  getTaxServiceByCode,
  getTaxProfile,
  getTaxServiceRequests,
  getUpcomingDeadlines,
  getOverdueDeadlines,
  checkServiceEligibility
} from './orgDocTaxServices';

// Import all mock data - Reports (Phase 4)
export {
  default as mockReports,
  mockOrganizationsList,
  mockExpiringDocumentsSummary,
  mockExpiringByType,
  mockExpiringDocumentsList,
  mockMissingDocumentsSummary,
  mockMissingByType,
  mockOrganizationsWithMissing,
  getExpiryStatusColor,
  getExpiryStatusLabel,
  getComplianceScoreColor,
  filterExpiringDocuments,
  filterMissingDocuments,
  generateExpiringDocumentsCsv,
  generateMissingDocumentsCsv,
  simulateDownload,
  simulateSendEmailReport
} from './orgDocReports';

/**
 * Check if we should use mock data
 * @returns {boolean}
 */
export const shouldUseMockData = () => {
  return ORG_DOC_USE_MOCK_DATA;
};

/**
 * Simulate API delay for mock data
 * @param {number} ms - Delay in milliseconds
 * @returns {Promise}
 */
export const simulateDelay = (ms = 500) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Generic mock API response wrapper
 * @param {*} data - Data to return
 * @param {number} delay - Optional delay in ms
 * @returns {Promise<{success: boolean, data: *}>}
 */
export const mockApiResponse = async (data, delay = 300) => {
  await simulateDelay(delay);
  return {
    success: true,
    data,
    _isMock: true
  };
};

/**
 * Mock paginated response
 * @param {Array} items - All items
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @returns {Object}
 */
export const mockPaginatedResponse = (items, page = 0, size = 10) => {
  const start = page * size;
  const end = start + size;
  const content = items.slice(start, end);

  return {
    content,
    page,
    size,
    totalElements: items.length,
    totalPages: Math.ceil(items.length / size),
    first: page === 0,
    last: end >= items.length,
    _isMock: true
  };
};
